import { enqueueSnackbar } from "notistack";
import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";

import FlexBox from "@/components/layout/Flexbox";
import {
  useCancelCustomCampaignMutation,
  useDeleteCustomCampaignMutation,
} from "@/pages/phishing-simulation/CustomCampaignV2/CustomCampaignApi";

export default function CustomCampaignList({ listCampaigns }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [deleteCustomCampaign] = useDeleteCustomCampaignMutation();
  const [cancelCustomCampaign] = useCancelCustomCampaignMutation();

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const handleClick = (event, row) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const mutateAndClose = async (mutation: CallableFunction) => {
    try {
      await mutation();
    } catch (error) {
      enqueueSnackbar("An unknown error occurred", { variant: "error" });
    }
    handleClose();
  };

  const handleDelete = async (campaignId: number) =>
    mutateAndClose(() => deleteCustomCampaign(campaignId));

  const handleCancel = async (campaignId: number) =>
    mutateAndClose(() => cancelCustomCampaign(campaignId));

  function handleViewCustomCampaign(params) {
    let pathname = location.pathname + "/details";

    if (params.state === "draft") {
      pathname = location.pathname + "/create";
    }

    navigate({
      pathname: pathname,
      search: "?id=" + params.id,
    });
  }

  const columns: GridColDef[] = [
    {
      field: "campaign_name",
      headerName: t("Campaign Name"),
      flex: 1,
      renderCell: (params) => {
        return (
          <FlexBox>
            <Typography>{params.value}</Typography>
            {params.row.state === "draft" && (
              <Typography
                variant={"body_small"}
                color={"primary.main"}
                sx={{ paddingLeft: "0.25rem", fontWeight: "bold" }}
              >
                {`- ${t("Draft")}`}
              </Typography>
            )}
            {params.row.state === "cancelled" && (
              <Typography
                variant={"body_small"}
                color={"warning.dark"}
                sx={{ paddingLeft: "0.25rem", fontWeight: "bold" }}
              >
                {`- ${t("Cancelled")}`}
              </Typography>
            )}
          </FlexBox>
        );
      },
    },
    { field: "start_date", headerName: t("Start date"), flex: 0.2 },
    { field: "end_date", headerName: t("End date"), flex: 0.2 },
    {
      field: "simulations_sent_count",
      headerName: t("Sent"),
      flex: 0.2,
      renderCell: (params) => {
        if (params.row.state === "draft") {
          return "-";
        }
        return params.value + " / " + params.row.total_scheduled_simulations;
      },
    },
    {
      field: "simulations_click_count",
      headerName: t("Clicked"),
      flex: 0.2,
    },
    {
      field: "simulations_reported_count",
      headerName: t("Reported"),
      flex: 0.2,
    },
    {
      field: "simulations_learning_followed_count",
      headerName: t("micro-learning followed"),
      flex: 0.2,
    },
    {
      field: "actions",
      width: 20,
      renderHeader: () => {
        return "";
      },
      renderCell: (params) => {
        return (
          <IconButton onClick={(event) => handleClick(event, params.row)}>
            <MoreHorizIcon />
          </IconButton>
        );
      },
    },
  ];

  return (
    <>
      <DataGrid
        rows={listCampaigns}
        onRowClick={(params) => handleViewCustomCampaign(params.row)}
        sx={{
          height: "100%",
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "bold",
          },
        }}
        columns={columns}
        disableColumnMenu
        initialState={{
          sorting: {
            sortModel: [
              {
                field: "start_date",
                sort: "desc",
              },
            ],
          },
          pagination: {
            paginationModel: {
              pageSize: 20,
            },
          },
        }}
        pageSizeOptions={[10, 20, 50, 100]}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => handleViewCustomCampaign(selectedRow)}>
          <VisibilityIcon color={"primary"} sx={{ marginRight: "0.5rem" }} />
          {t("View Campaign")}
        </MenuItem>
        {selectedRow?.state === "draft" && (
          <MenuItem onClick={() => handleDelete(selectedRow.id)}>
            <DeleteIcon sx={{ color: "red", marginRight: "0.5rem" }} />
            {t("Delete")}
          </MenuItem>
        )}
        {selectedRow?.state === "scheduled" && (
          <MenuItem onClick={() => handleCancel(selectedRow.id)}>
            <CancelIcon sx={{ color: "warning.dark", marginRight: "0.5rem" }} />
            {t("Cancel")}
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
